import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import apiService from '../services/api.service';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import type {
    AllocationType,
    CreatePaymentRequestInput,
    PaymentRequest,
    PaymentRequestStatus,
    PaymentType,
} from '../types/paymentRequest';
import PaymentRequestStatusChip from './components/PaymentRequestStatusChip';
import PaymentRequestAttachments from './components/PaymentRequestAttachments';

// ---------------------------------------------------------------------------
// Lookup types (shape returned by /payment-requests/lookups/*)
// ---------------------------------------------------------------------------

interface ProjectOption {
    code: string;
    name: string | null;
    division: string | null;
}

interface CostCenterOption {
    code: string;
    name: string | null;
}

interface VendorOption {
    cardcode: string;
    cardname: string | null;
    currency: string | null;
}

interface POOption {
    docentry: number;
    docnum: string | null;
    docdate: string | null;
    cardcode: string | null;
    doctotal: number | string;
    doccurrency: string | null;
    status: string | null;
}

interface GRPOOption {
    docentry: number;
    docnum: string | null;
    docdate: string | null;
    cardcode: string | null;
    doctotal: number | string;
}

interface POPaymentStatus {
    poDocNum: string;
    poTotal: number;
    currency: string;
    sumActivePRAmount: number;
    remainingOpen: number;
    activePRList: any[];
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
    allocationType: AllocationType;
    project: ProjectOption | null;
    costCenter: CostCenterOption | null;
    vendor: VendorOption | null;
    paymentType: PaymentType;
    po: POOption | null;
    grpoSelection: Record<number, GRPOOption>;
    invoiceNumber: string;
    invoiceDate: string;
    amountFigures: string; // string for input; parsed on submit
    payBy: string;
    initiatorRemarks: string;
}

const emptyForm: FormState = {
    allocationType: 'Project',
    project: null,
    costCenter: null,
    vendor: null,
    paymentType: 'Advance',
    po: null,
    grpoSelection: {},
    invoiceNumber: '',
    invoiceDate: '',
    amountFigures: '',
    payBy: '',
    initiatorRemarks: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const todayISO = (): string => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const formatMoney = (value: number, currency: string | null | undefined): string => {
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));
    return currency ? `${formatted} ${currency}` : formatted;
};

const toDateOnly = (value: string | null | undefined): string => {
    if (!value) return '';
    // Accept "YYYY-MM-DD" or ISO timestamp; cut to first 10 chars when possible.
    const s = String(value);
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PaymentRequestCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id: idParam } = useParams<{ id?: string }>();
    const editingId = idParam ? Number(idParam) : null;
    const isEditing = editingId !== null && Number.isFinite(editingId);

    const user = useSelector((s: RootState) => s.auth.user);

    const companyId = useMemo<number | null>(() => {
        const raw = Number(localStorage.getItem('selectedCompanyId') || '');
        return Number.isFinite(raw) && raw > 0 ? raw : null;
    }, []);

    const [form, setForm] = useState<FormState>(emptyForm);
    const [status, setStatus] = useState<PaymentRequestStatus>('Draft');
    const [loadedCurrency, setLoadedCurrency] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(isEditing);
    const [saving, setSaving] = useState<boolean>(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

    // Lookup option lists
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenterOption[]>([]);
    const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
    const [vendorInput, setVendorInput] = useState<string>('');
    const [vendorLoading, setVendorLoading] = useState<boolean>(false);
    const [poOptions, setPOOptions] = useState<POOption[]>([]);
    const [poLoading, setPOLoading] = useState<boolean>(false);
    const [poPaymentStatus, setPOPaymentStatus] = useState<POPaymentStatus | null>(null);
    const [grpoOptions, setGRPOOptions] = useState<GRPOOption[]>([]);
    const [grpoLoading, setGRPOLoading] = useState<boolean>(false);

    // -----------------------------------------------------------------------
    // Load draft when editing
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!isEditing || !editingId) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const res = await apiService.getPaymentRequest(editingId);
                if (cancelled) return;
                const pr: PaymentRequest = res?.data ?? res;
                if (!pr) {
                    setPageError('Payment request not found.');
                    return;
                }
                setStatus(pr.status);
                setLoadedCurrency(pr.amountCurrency || null);
                setForm((prev) => ({
                    ...prev,
                    allocationType: pr.allocationType,
                    project:
                        pr.allocationType === 'Project' && pr.projectCode
                            ? {
                                  code: pr.projectCode,
                                  name: null,
                                  division: pr.divisionCode,
                              }
                            : null,
                    costCenter:
                        pr.allocationType === 'CostCenter' && pr.costCenterCode
                            ? { code: pr.costCenterCode, name: null }
                            : null,
                    vendor: pr.vendorCardCode
                        ? {
                              cardcode: pr.vendorCardCode,
                              cardname: null,
                              currency: pr.amountCurrency || null,
                          }
                        : null,
                    paymentType: pr.paymentType,
                    po: pr.poDocEntry
                        ? {
                              docentry: pr.poDocEntry,
                              docnum: null,
                              docdate: null,
                              cardcode: pr.vendorCardCode,
                              doctotal: 0,
                              doccurrency: pr.amountCurrency || null,
                              status: null,
                          }
                        : null,
                    grpoSelection: {},
                    invoiceNumber: pr.invoiceNumber || '',
                    invoiceDate: toDateOnly(pr.invoiceDate),
                    amountFigures: String(pr.amountFigures ?? ''),
                    payBy: toDateOnly(pr.payBy),
                    initiatorRemarks: pr.initiatorRemarks || '',
                }));
            } catch (err: any) {
                if (!cancelled) {
                    setPageError(err?.response?.data?.message || err?.message || 'Failed to load draft.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [editingId, isEditing]);

    // -----------------------------------------------------------------------
    // Lookup: Projects / Cost Centers
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!companyId) return;
        if (form.allocationType !== 'Project') return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.lookupPRProjects(companyId);
                if (cancelled) return;
                setProjects(Array.isArray(res?.data) ? res.data : []);
            } catch {
                if (!cancelled) setProjects([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, form.allocationType]);

    useEffect(() => {
        if (!companyId) return;
        if (form.allocationType !== 'CostCenter') return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.lookupPRCostCenters(companyId);
                if (cancelled) return;
                setCostCenters(Array.isArray(res?.data) ? res.data : []);
            } catch {
                if (!cancelled) setCostCenters([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, form.allocationType]);

    // -----------------------------------------------------------------------
    // Lookup: Vendors (debounced)
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!companyId) return;
        const handle = setTimeout(async () => {
            try {
                setVendorLoading(true);
                const res = await apiService.lookupPRVendors({
                    companyId,
                    q: vendorInput || undefined,
                    limit: 50,
                });
                setVendorOptions(Array.isArray(res?.data) ? res.data : []);
            } catch {
                setVendorOptions([]);
            } finally {
                setVendorLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [companyId, vendorInput]);

    // -----------------------------------------------------------------------
    // Lookup: POs (depends on vendor + project)
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!companyId) return;
        if (!form.vendor) {
            setPOOptions([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setPOLoading(true);
                const res = await apiService.lookupPRPurchaseOrders({
                    companyId,
                    vendorCardCode: form.vendor!.cardcode,
                    projectCode: form.project?.code,
                });
                if (cancelled) return;
                setPOOptions(Array.isArray(res?.data) ? res.data : []);
            } catch {
                if (!cancelled) setPOOptions([]);
            } finally {
                if (!cancelled) setPOLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, form.vendor, form.project]);

    // -----------------------------------------------------------------------
    // PO Payment Status (when PO selected)
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!companyId || !form.po) {
            setPOPaymentStatus(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.lookupPRPOPaymentStatus(companyId, form.po!.docentry);
                if (cancelled) return;
                setPOPaymentStatus(res?.data ?? null);
            } catch {
                if (!cancelled) setPOPaymentStatus(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, form.po]);

    // -----------------------------------------------------------------------
    // GRPOs (when AgainstGRPO + PO selected)
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!companyId || !form.po || form.paymentType !== 'AgainstGRPO') {
            setGRPOOptions([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setGRPOLoading(true);
                const res = await apiService.lookupPRGRPOs({
                    companyId,
                    poDocEntry: form.po!.docentry,
                });
                if (cancelled) return;
                setGRPOOptions(Array.isArray(res?.data) ? res.data : []);
            } catch {
                if (!cancelled) setGRPOOptions([]);
            } finally {
                if (!cancelled) setGRPOLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, form.po, form.paymentType]);

    // -----------------------------------------------------------------------
    // Auto-set amount for AgainstGRPO from selected GRPOs
    // -----------------------------------------------------------------------

    const grpoSelectedTotal = useMemo<number>(() => {
        return Object.values(form.grpoSelection).reduce(
            (sum, g) => sum + Number(g.doctotal || 0),
            0,
        );
    }, [form.grpoSelection]);

    useEffect(() => {
        if (form.paymentType === 'AgainstGRPO') {
            setForm((prev) => ({
                ...prev,
                amountFigures: grpoSelectedTotal > 0 ? String(grpoSelectedTotal.toFixed(2)) : '',
            }));
        }
    }, [grpoSelectedTotal, form.paymentType]);

    // -----------------------------------------------------------------------
    // Derived
    // -----------------------------------------------------------------------

    const currency = useMemo<string | null>(() => {
        return (
            poPaymentStatus?.currency ||
            form.po?.doccurrency ||
            form.vendor?.currency ||
            loadedCurrency ||
            null
        );
    }, [poPaymentStatus, form.po, form.vendor, loadedCurrency]);

    const remainingOpen = poPaymentStatus?.remainingOpen ?? null;
    const isLocked = isEditing && status !== 'Draft' && status !== 'Returned';

    // -----------------------------------------------------------------------
    // Validation
    // -----------------------------------------------------------------------

    const validate = useCallback(
        (forSubmit: boolean): Record<string, string> => {
            const next: Record<string, string> = {};
            if (!companyId) next.companyId = 'No company selected. Pick a company first.';
            if (!form.paymentType) next.paymentType = 'Required.';
            if (!form.allocationType) next.allocationType = 'Required.';

            if (forSubmit) {
                if (form.allocationType === 'Project' && !form.project) {
                    next.project = 'Select a project.';
                } else if (form.allocationType === 'CostCenter' && !form.costCenter) {
                    next.costCenter = 'Select a cost center.';
                }
                if (!form.vendor) next.vendor = 'Select a vendor.';
                if (!form.po) next.po = 'Select a Purchase Order.';

                if (form.paymentType === 'AgainstGRPO') {
                    const count = Object.keys(form.grpoSelection).length;
                    if (count === 0) {
                        next.grpoSelection = 'Select at least one GRPO.';
                    }
                }

                const amount = Number(form.amountFigures);
                if (!Number.isFinite(amount) || amount <= 0) {
                    next.amountFigures = 'Amount must be greater than zero.';
                } else if (
                    form.paymentType === 'Advance' &&
                    remainingOpen !== null &&
                    amount > remainingOpen + 0.005
                ) {
                    next.amountFigures = `Amount exceeds remaining open balance (${formatMoney(
                        remainingOpen,
                        currency,
                    )}).`;
                }

                if (!form.payBy) {
                    next.payBy = 'Required.';
                } else {
                    const pay = new Date(form.payBy);
                    const today = new Date(todayISO());
                    if (Number.isNaN(pay.getTime()) || pay < today) {
                        next.payBy = 'Pay-by date must be today or later.';
                    }
                }
            } else {
                // For draft we still want amount within remaining if specified.
                const amount = Number(form.amountFigures);
                if (
                    form.amountFigures &&
                    Number.isFinite(amount) &&
                    form.paymentType === 'Advance' &&
                    remainingOpen !== null &&
                    amount > remainingOpen + 0.005
                ) {
                    next.amountFigures = `Amount exceeds remaining open balance (${formatMoney(
                        remainingOpen,
                        currency,
                    )}).`;
                }
            }

            if (form.initiatorRemarks && form.initiatorRemarks.length > 4000) {
                next.initiatorRemarks = 'Maximum 4000 characters.';
            }

            return next;
        },
        [companyId, form, remainingOpen, currency],
    );

    // -----------------------------------------------------------------------
    // Build payload
    // -----------------------------------------------------------------------

    const buildPayload = useCallback((): CreatePaymentRequestInput | null => {
        if (!companyId) return null;
        const amount = Number(form.amountFigures || 0);
        const payload: CreatePaymentRequestInput = {
            companyId,
            paymentType: form.paymentType,
            allocationType: form.allocationType,
            vendorCardCode: form.vendor?.cardcode || '',
            poDocEntry: form.po?.docentry || 0,
            amountFigures: Number.isFinite(amount) ? amount : 0,
            payBy: form.payBy || '',
        };
        if (form.allocationType === 'Project' && form.project) {
            payload.projectCode = form.project.code;
            if (form.project.division) payload.divisionCode = form.project.division;
        }
        if (form.allocationType === 'CostCenter' && form.costCenter) {
            payload.costCenterCode = form.costCenter.code;
        }
        if (form.paymentType === 'AgainstGRPO') {
            payload.grpoDocEntries = Object.keys(form.grpoSelection).map(Number);
        }
        if (form.invoiceNumber) payload.invoiceNumber = form.invoiceNumber;
        if (form.invoiceDate) payload.invoiceDate = form.invoiceDate;
        if (form.initiatorRemarks) payload.initiatorRemarks = form.initiatorRemarks;
        return payload;
    }, [companyId, form]);

    // -----------------------------------------------------------------------
    // Save / Submit handlers
    // -----------------------------------------------------------------------

    const handleSaveDraft = async (): Promise<number | null> => {
        const v = validate(false);
        setErrors(v);
        if (Object.keys(v).length > 0) return null;
        const payload = buildPayload();
        if (!payload) return null;

        try {
            setSaving(true);
            setPageError(null);
            let savedId: number | null = null;
            if (isEditing && editingId) {
                await apiService.updatePaymentRequest(editingId, payload);
                savedId = editingId;
            } else {
                const res = await apiService.createPaymentRequest(payload);
                const pr: PaymentRequest = res?.data ?? res;
                savedId = pr?.paymentRequestId ?? null;
            }
            if (savedId) {
                navigate(`/payment-requests/${savedId}`);
            }
            return savedId;
        } catch (err: any) {
            setPageError(err?.response?.data?.message || err?.message || 'Failed to save draft.');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitClicked = () => {
        const v = validate(true);
        setErrors(v);
        if (Object.keys(v).length > 0) return;
        setConfirmOpen(true);
    };

    const handleConfirmedSubmit = async () => {
        setConfirmOpen(false);
        const payload = buildPayload();
        if (!payload) return;

        try {
            setSaving(true);
            setPageError(null);
            let savedId: number | null = editingId;
            if (isEditing && editingId) {
                await apiService.updatePaymentRequest(editingId, payload);
            } else {
                const res = await apiService.createPaymentRequest(payload);
                const pr: PaymentRequest = res?.data ?? res;
                savedId = pr?.paymentRequestId ?? null;
            }
            if (!savedId) {
                setPageError('Could not determine saved request id. Aborting submit.');
                return;
            }
            await apiService.submitPaymentRequest(savedId);
            navigate(`/payment-requests/${savedId}`);
        } catch (err: any) {
            setPageError(err?.response?.data?.message || err?.message || 'Failed to submit.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => navigate('/payment-requests');

    // -----------------------------------------------------------------------
    // Field setters
    // -----------------------------------------------------------------------

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleAllocationTypeChange = (
        _e: React.MouseEvent<HTMLElement>,
        value: AllocationType | null,
    ) => {
        if (!value) return;
        setForm((prev) => ({
            ...prev,
            allocationType: value,
            project: null,
            costCenter: null,
            po: null,
            grpoSelection: {},
        }));
    };

    const handlePaymentTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as PaymentType;
        setForm((prev) => ({
            ...prev,
            paymentType: value,
            grpoSelection: {},
            amountFigures: value === 'AgainstGRPO' ? '' : prev.amountFigures,
        }));
    };

    const toggleGRPO = (g: GRPOOption) => {
        setForm((prev) => {
            const next = { ...prev.grpoSelection };
            if (next[g.docentry]) {
                delete next[g.docentry];
            } else {
                next[g.docentry] = g;
            }
            return { ...prev, grpoSelection: next };
        });
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: 400 }}>
                <CircularProgress />
            </Stack>
        );
    }

    const titleText = isEditing ? 'Edit Payment Request' : 'New Payment Request';

    return (
        <Paper
            component="section"
            aria-labelledby="prc-heading"
            elevation={0}
            sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                minHeight: 'calc(100vh - 100px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            {/* Header card */}
            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <Typography
                            component="h1"
                            id="prc-heading"
                            variant="h5"
                            sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 22, md: 24 } }}
                        >
                            {titleText}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Status:
                            </Typography>
                            <PaymentRequestStatusChip status={status} />
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {!companyId && (
                <Alert severity="error">
                    No company selected. Pick a company from the top bar before creating a Payment
                    Request.
                </Alert>
            )}

            {pageError && (
                <Alert severity="error" onClose={() => setPageError(null)}>
                    {pageError}
                </Alert>
            )}

            {isLocked && (
                <Alert severity="warning">
                    This Payment Request is in status <strong>{status}</strong> and cannot be
                    edited.
                </Alert>
            )}

            {/* Allocation */}
            <Card variant="outlined">
                <CardHeader title="Allocation" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <Stack spacing={2}>
                        <ToggleButtonGroup
                            color="primary"
                            value={form.allocationType}
                            exclusive
                            onChange={handleAllocationTypeChange}
                            disabled={isLocked}
                            size="small"
                        >
                            <ToggleButton value="Project">Project</ToggleButton>
                            <ToggleButton value="CostCenter">Cost Center</ToggleButton>
                        </ToggleButtonGroup>

                        {form.allocationType === 'Project' ? (
                            <Autocomplete
                                options={projects}
                                value={form.project}
                                onChange={(_e, v) => setField('project', v)}
                                getOptionLabel={(o) => (o ? `${o.code} - ${o.name ?? ''}` : '')}
                                isOptionEqualToValue={(a, b) => a.code === b.code}
                                disabled={isLocked}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Project"
                                        size="small"
                                        error={Boolean(errors.project)}
                                        helperText={errors.project || ''}
                                    />
                                )}
                            />
                        ) : (
                            <Autocomplete
                                options={costCenters}
                                value={form.costCenter}
                                onChange={(_e, v) => setField('costCenter', v)}
                                getOptionLabel={(o) => (o ? `${o.code} - ${o.name ?? ''}` : '')}
                                isOptionEqualToValue={(a, b) => a.code === b.code}
                                disabled={isLocked}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Cost Center"
                                        size="small"
                                        error={Boolean(errors.costCenter)}
                                        helperText={errors.costCenter || ''}
                                    />
                                )}
                            />
                        )}

                        {form.allocationType === 'Project' && form.project && (
                            <Stack direction="row" spacing={1}>
                                <Chip size="small" label={`Code: ${form.project.code}`} />
                                {form.project.name && (
                                    <Chip size="small" label={`Name: ${form.project.name}`} />
                                )}
                                {form.project.division && (
                                    <Chip
                                        size="small"
                                        label={`Division: ${form.project.division}`}
                                    />
                                )}
                            </Stack>
                        )}
                        {form.allocationType === 'CostCenter' && form.costCenter && (
                            <Stack direction="row" spacing={1}>
                                <Chip size="small" label={`Code: ${form.costCenter.code}`} />
                                {form.costCenter.name && (
                                    <Chip size="small" label={`Name: ${form.costCenter.name}`} />
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Requested by */}
            <Card variant="outlined">
                <CardHeader title="Requested By" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Typography variant="body1">
                            <strong>{user?.fullname || '—'}</strong>
                        </Typography>
                        {user?.role && <Chip size="small" label={user.role} />}
                        {user?.department && (
                            <Chip size="small" variant="outlined" label={user.department} />
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Beneficiary (Vendor) */}
            <Card variant="outlined">
                <CardHeader title="Beneficiary (Vendor)" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={vendorOptions}
                            value={form.vendor}
                            onChange={(_e, v) => {
                                setForm((prev) => ({
                                    ...prev,
                                    vendor: v,
                                    po: null,
                                    grpoSelection: {},
                                    amountFigures:
                                        prev.paymentType === 'AgainstGRPO' ? '' : prev.amountFigures,
                                }));
                            }}
                            inputValue={vendorInput}
                            onInputChange={(_e, v) => setVendorInput(v)}
                            getOptionLabel={(o) =>
                                o ? `${o.cardcode} - ${o.cardname ?? ''}` : ''
                            }
                            isOptionEqualToValue={(a, b) => a.cardcode === b.cardcode}
                            loading={vendorLoading}
                            disabled={isLocked || !companyId}
                            filterOptions={(x) => x}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Vendor"
                                    size="small"
                                    error={Boolean(errors.vendor)}
                                    helperText={errors.vendor || ''}
                                />
                            )}
                        />
                        {form.vendor && (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip size="small" label={`CardCode: ${form.vendor.cardcode}`} />
                                {form.vendor.cardname && (
                                    <Chip
                                        size="small"
                                        label={`Name: ${form.vendor.cardname}`}
                                    />
                                )}
                                {form.vendor.currency && (
                                    <Chip
                                        size="small"
                                        label={`Currency: ${form.vendor.currency}`}
                                    />
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Payment Type */}
            <Card variant="outlined">
                <CardHeader title="Payment Type" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <FormControl error={Boolean(errors.paymentType)} disabled={isLocked}>
                        <FormLabel>Type</FormLabel>
                        <RadioGroup
                            row
                            value={form.paymentType}
                            onChange={handlePaymentTypeChange}
                        >
                            <FormControlLabel
                                value="Advance"
                                control={<Radio />}
                                label="Advance Payment (against PO only)"
                            />
                            <FormControlLabel
                                value="AgainstGRPO"
                                control={<Radio />}
                                label="Payment against GRPO(s)"
                            />
                        </RadioGroup>
                        {errors.paymentType && (
                            <FormHelperText>{errors.paymentType}</FormHelperText>
                        )}
                    </FormControl>
                </CardContent>
            </Card>

            {/* Purchase Order */}
            <Card variant="outlined">
                <CardHeader title="Purchase Order" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={poOptions}
                            value={form.po}
                            onChange={(_e, v) => {
                                setForm((prev) => ({
                                    ...prev,
                                    po: v,
                                    grpoSelection: {},
                                    amountFigures:
                                        prev.paymentType === 'AgainstGRPO' ? '' : prev.amountFigures,
                                }));
                            }}
                            getOptionLabel={(o) =>
                                o
                                    ? `#${o.docnum ?? o.docentry} - ${formatMoney(
                                          Number(o.doctotal),
                                          o.doccurrency,
                                      )}`
                                    : ''
                            }
                            isOptionEqualToValue={(a, b) => a.docentry === b.docentry}
                            loading={poLoading}
                            disabled={isLocked || !form.vendor}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Purchase Order"
                                    size="small"
                                    error={Boolean(errors.po)}
                                    helperText={
                                        errors.po ||
                                        (!form.vendor
                                            ? 'Select a vendor first.'
                                            : poOptions.length === 0 && !poLoading
                                              ? 'No POs found for this vendor / project.'
                                              : '')
                                    }
                                />
                            )}
                        />

                        {form.po && poPaymentStatus && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 1,
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                    p: 2,
                                    bgcolor: '#fafafa',
                                    border: '1px solid #eee',
                                    borderRadius: 1,
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        PO Total
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {formatMoney(
                                            poPaymentStatus.poTotal,
                                            poPaymentStatus.currency,
                                        )}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Already paid (active PRs)
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {formatMoney(
                                            poPaymentStatus.sumActivePRAmount,
                                            poPaymentStatus.currency,
                                        )}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Remaining open
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {formatMoney(
                                            poPaymentStatus.remainingOpen,
                                            poPaymentStatus.currency,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* GRPO multi-picker */}
            {form.paymentType === 'AgainstGRPO' && (
                <Card variant="outlined">
                    <CardHeader title="GRPOs" titleTypographyProps={{ variant: 'h6' }} />
                    <CardContent>
                        {!form.po ? (
                            <Alert severity="info">Select a Purchase Order first.</Alert>
                        ) : grpoLoading ? (
                            <Stack alignItems="center" sx={{ py: 2 }}>
                                <CircularProgress size={24} />
                            </Stack>
                        ) : grpoOptions.length === 0 ? (
                            <Alert severity="info">
                                All GRPOs for this PO are already linked to active Payment
                                Requests, or none have been received yet.
                            </Alert>
                        ) : (
                            <Stack spacing={1}>
                                {grpoOptions.map((g) => {
                                    const checked = Boolean(form.grpoSelection[g.docentry]);
                                    return (
                                        <Box
                                            key={g.docentry}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns:
                                                    '40px 1fr 1fr 1fr 1fr',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 1,
                                                py: 0.5,
                                                border: '1px solid #eee',
                                                borderRadius: 1,
                                                bgcolor: checked ? '#f0f7ff' : 'transparent',
                                            }}
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => toggleGRPO(g)}
                                                disabled={isLocked}
                                                size="small"
                                            />
                                            <Typography variant="body2">
                                                #{g.docnum ?? g.docentry}
                                            </Typography>
                                            <Typography variant="body2">
                                                {toDateOnly(g.docdate)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {g.cardcode}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ textAlign: 'right' }}
                                            >
                                                {formatMoney(Number(g.doctotal), currency)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                <Divider sx={{ my: 1 }} />
                                <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Selected total:
                                    </Typography>
                                    <Typography variant="body1" fontWeight={700}>
                                        {formatMoney(grpoSelectedTotal, currency)}
                                    </Typography>
                                </Stack>
                                {errors.grpoSelection && (
                                    <FormHelperText error>{errors.grpoSelection}</FormHelperText>
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Invoice & Amount */}
            <Card variant="outlined">
                <CardHeader title="Invoice & Amount" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        }}
                    >
                        <TextField
                            size="small"
                            label="Invoice No."
                            value={form.invoiceNumber}
                            onChange={(e) => setField('invoiceNumber', e.target.value)}
                            disabled={isLocked}
                        />
                        <TextField
                            size="small"
                            label="Invoice Date"
                            type="date"
                            value={form.invoiceDate}
                            onChange={(e) => setField('invoiceDate', e.target.value)}
                            disabled={isLocked}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            size="small"
                            label="Amount"
                            type="number"
                            value={form.amountFigures}
                            onChange={(e) => setField('amountFigures', e.target.value)}
                            disabled={isLocked || form.paymentType === 'AgainstGRPO'}
                            InputProps={{
                                endAdornment: currency ? (
                                    <Chip size="small" label={currency} sx={{ ml: 1 }} />
                                ) : null,
                            }}
                            error={Boolean(errors.amountFigures)}
                            helperText={
                                errors.amountFigures ||
                                (form.paymentType === 'AgainstGRPO'
                                    ? 'Auto-calculated from selected GRPOs.'
                                    : remainingOpen !== null
                                      ? `Remaining open: ${formatMoney(remainingOpen, currency)}`
                                      : '')
                            }
                        />
                        <TextField
                            size="small"
                            label="Pay By"
                            type="date"
                            value={form.payBy}
                            onChange={(e) => setField('payBy', e.target.value)}
                            disabled={isLocked}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: todayISO() }}
                            error={Boolean(errors.payBy)}
                            helperText={errors.payBy || ''}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Initiator Remarks */}
            <Card variant="outlined">
                <CardHeader title="Initiator Remarks" titleTypographyProps={{ variant: 'h6' }} />
                <CardContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={form.initiatorRemarks}
                        onChange={(e) => setField('initiatorRemarks', e.target.value)}
                        disabled={isLocked}
                        inputProps={{ maxLength: 4000 }}
                        helperText={
                            errors.initiatorRemarks ||
                            `${form.initiatorRemarks.length} / 4000 characters`
                        }
                        error={Boolean(errors.initiatorRemarks)}
                    />
                </CardContent>
            </Card>

            {/* Attachments — visible after first save (needs an id) */}
            <PaymentRequestAttachments
                paymentRequestId={isEditing ? editingId : null}
                canEdit={!isLocked}
                defaultStage="Initiator"
            />

            {/* Form actions */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="flex-end"
                sx={{ pt: 1 }}
            >
                <Button variant="text" onClick={handleCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleSaveDraft}
                    disabled={saving || isLocked || !companyId}
                >
                    {saving ? 'Saving…' : 'Save Draft'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmitClicked}
                    disabled={saving || isLocked || !companyId}
                >
                    Submit to AP Team →
                </Button>
            </Stack>

            {/* Confirm dialog */}
            <ResponsiveDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                aria-labelledby="prc-submit-title"
                mobileFullScreen={false}
            >
                <DialogTitle id="prc-submit-title">Submit Payment Request?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Once submitted, you cannot edit until returned. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmedSubmit}
                        disabled={saving}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </ResponsiveDialog>
        </Paper>
    );
};

export default PaymentRequestCreate;
